import type { DefaultContext, HttpOptions, NextLink, Operation } from '@apollo/client';

import { ApolloLink, HttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

import { env } from '@/environment';
import { AppAuthenticator, TokenType } from '@/helpers/api/tokens';
import { getAuthFromStorage } from '@/helpers/auth/core';
import { isServerSide } from '@/helpers/isServerSide';
import { type TWithResponseHeaders } from '@/infra/types/api';

const errorLink = onError(({ graphQLErrors, networkError, protocolErrors }) => {
  if (graphQLErrors)
    graphQLErrors.forEach((error) => {
      const { locations, message, path } = error;
      console.error(
        `[Apollo / GraphQL error]:`,
        `Message: ${message}`,
        `Location: ${JSON.stringify(locations)},`,
        `Path: ${path},`,
      );
    });

  if (protocolErrors) {
    protocolErrors.forEach(({ extensions, message }) => {
      console.error(`[Apollo / Protocol error]: Message: ${message}, Extensions: ${JSON.stringify(extensions)}`);
    });
  }

  if (networkError) {
    console.error(`[Apollo / Network error]: ${networkError}`);
  }
});

const makeApolloLink = (httpOptions?: HttpOptions, isOmitBuiltinAuth?: boolean): ApolloLink => {
  const httpLink = new HttpLink({
    ...httpOptions,
    fetchOptions: {
      credentials: 'same-origin',
    },
    uri: `${env.GRAPHQL_URL}`,
  });
  const authContext = !isOmitBuiltinAuth
    ? setContext(async (_, context: DefaultContext): Promise<DefaultContext> => {
        let finalToken = await AppAuthenticator.getInstance().getFreshAccessToken({ tokenType: TokenType.guest });
        const authToken = getAuthFromStorage()?.accessToken;

        if (authToken) {
          const expirationTime = AppAuthenticator.getInstance().getTokenExpirationTime(authToken);

          if (expirationTime && expirationTime > Date.now() + 1000 * 60 * 60) {
            finalToken = authToken;
          }
        }

        return finalToken
          ? { ...context, headers: { ...context?.headers, Authorization: `bearer ${finalToken}` } }
          : context;
      })
    : undefined;

  const cookiesContext = setContext(
    (_, context: DefaultContext): DefaultContext => ({
      ...context,
      headers: {
        ...context?.headers,
        ...(!isServerSide() ? { Cookie: document.cookie } : {}),
      },
    }),
  );

  const responseContext = new ApolloLink((operation: Operation, forward: NextLink) =>
    forward(operation).map((fetchResult) => {
      const { onResponseHeaders, response } = operation.getContext() as TWithResponseHeaders & { response: Response };
      onResponseHeaders?.(response?.headers);
      return fetchResult;
    }),
  );

  return ApolloLink.from([errorLink, authContext, cookiesContext, responseContext, httpLink].filter(Boolean));
};

export default makeApolloLink;
